import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import tourAssistantIcon from '@/static/icons/logobsf_new.svg';
import {
  ENABLE_ISSUED_INVOICES_GUIDED_TOUR,
  ISSUED_INVOICES_GUIDED_TOUR_MAX_AUTO_SHOWS_WITHOUT_COMPLETION,
  ISSUED_INVOICES_GUIDED_TOUR_MAX_IN_PROGRESS_AUTO_RESHOWS,
  ISSUED_INVOICES_GUIDED_TOUR_RESHOW_INTERVAL_DAYS,
  ISSUED_INVOICES_GUIDED_TOUR_STORAGE_KEY,
} from '@/js/constants';

const TOUR_WAIT_TIMEOUT = 15000;
const TOUR_WAIT_INTERVAL = 120;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const TOUR_SKIP_BUTTON_CLASS = 'issued-invoices-tour-skip-btn';
const TOUR_POPOVER_CLASS = 'issued-invoices-tour-popover';
const TOUR_POPOVER_STYLES_ID = 'issued-invoices-tour-popover-styles';
const TOUR_POPOVER_WITH_IMAGE_CLASS =
  'issued-invoices-tour-popover--with-image';
const TOUR_SMALL_IMAGE_POPOVER_CLASS =
  'issued-invoices-tour-popover--small-image';
const ISSUED_INVOICES_DASHBOARD_BUTTON_SELECTOR =
  '[data-tour-id="issued-invoices-dashboard-button"]';
const ISSUED_INVOICES_TOP_MENU_OPTION_SELECTOR =
  '[data-tour-id="issued-invoices-top-menu-option"]';
const TOUR_ASSET_CONTEXTS = {
  img: require.context(
    '@/static/img',
    false,
    /\.(png|jpe?g|webp|svg)$/
  ),
};
const TOUR_IMAGE_FILE_PATTERN = /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i;
const TOUR_STEP_IMAGE_FALLBACKS = {
  listIntro: 'img/ListadoFacturas-ES.png',
  listActions: 'img/IconosListadoFacturas.png',
  detailsSummary: 'img/CabeceraFacturas-ES.png',
  detailsLines: 'img/DetallesLineasFactura-ES.png',
};
const ISSUED_INVOICES_TOUR_PENDING_LIST_START_KEY =
  'issued-invoices-tour-pending-list-start';
const ISSUED_INVOICES_TOUR_PENDING_DETAILS_START_KEY =
  'issued-invoices-tour-pending-details-start';

let activeDriver = null;
let activeDriverOwnerVm = null;
let lockedScrollY = 0;
let previousHtmlOverflow = '';
let previousBodyOverflow = '';
let previousBodyPosition = '';
let previousBodyTop = '';
let previousBodyWidth = '';
let lockedScrollableElements = [];
const TOUR_CLICK_BLOCK_EVENTS = [
  'click',
  'dblclick',
  'mousedown',
  'mouseup',
  'pointerdown',
  'pointerup',
  'touchstart',
  'touchend',
];

const TOUR_SCROLLABLE_SELECTORS = [
  '.view-main',
  '.views',
  '.page',
  '.page-content',
  '.safe-areas',
];

const SCROLL_BLOCK_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
  'Spacebar',
]);

function preventTourScroll(event) {
  event.preventDefault();
}

function preventTourScrollKeys(event) {
  if (SCROLL_BLOCK_KEYS.has(event.key)) {
    event.preventDefault();
  }
}

function preventClicksOutsideTour(event) {
  if (!event.isTrusted) {
    return;
  }

  const targetElement =
    event.target instanceof Element
      ? event.target
      : event.target?.parentElement;

  if (targetElement?.closest?.('.driver-popover')) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function lockTourScroll() {
  const html = document.documentElement;
  const body = document.body;

  if (
    !html ||
    !body ||
    body.dataset.issuedInvoicesTourScrollLocked === 'true'
  ) {
    return;
  }

  lockedScrollY = window.scrollY || window.pageYOffset || 0;
  previousHtmlOverflow = html.style.overflow;
  previousBodyOverflow = body.style.overflow;
  previousBodyPosition = body.style.position;
  previousBodyTop = body.style.top;
  previousBodyWidth = body.style.width;

  html.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.width = '100%';
  body.dataset.issuedInvoicesTourScrollLocked = 'true';

  lockedScrollableElements = Array.from(
    document.querySelectorAll(TOUR_SCROLLABLE_SELECTORS.join(','))
  ).map((element) => {
    const previousOverflow = element.style.overflow;
    const previousOverflowY = element.style.overflowY;
    const previousTouchAction = element.style.touchAction;

    element.style.overflow = 'hidden';
    element.style.overflowY = 'hidden';
    element.style.touchAction = 'none';

    return {
      element,
      previousOverflow,
      previousOverflowY,
      previousTouchAction,
    };
  });

  window.addEventListener('wheel', preventTourScroll, {
    passive: false,
  });
  window.addEventListener('touchmove', preventTourScroll, {
    passive: false,
  });
  window.addEventListener('keydown', preventTourScrollKeys, {
    passive: false,
  });
  TOUR_CLICK_BLOCK_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, preventClicksOutsideTour, {
      capture: true,
      passive: false,
    });
  });
}

function unlockTourScroll() {
  const html = document.documentElement;
  const body = document.body;

  if (
    !html ||
    !body ||
    body.dataset.issuedInvoicesTourScrollLocked !== 'true'
  ) {
    return;
  }

  html.style.overflow = previousHtmlOverflow;
  body.style.overflow = previousBodyOverflow;
  body.style.position = previousBodyPosition;
  body.style.top = previousBodyTop;
  body.style.width = previousBodyWidth;
  delete body.dataset.issuedInvoicesTourScrollLocked;
  lockedScrollableElements.forEach(
    ({
      element,
      previousOverflow,
      previousOverflowY,
      previousTouchAction,
    }) => {
      element.style.overflow = previousOverflow;
      element.style.overflowY = previousOverflowY;
      element.style.touchAction = previousTouchAction;
    }
  );
  lockedScrollableElements = [];
  window.removeEventListener('wheel', preventTourScroll);
  window.removeEventListener('touchmove', preventTourScroll);
  window.removeEventListener('keydown', preventTourScrollKeys);
  TOUR_CLICK_BLOCK_EVENTS.forEach((eventName) => {
    document.removeEventListener(
      eventName,
      preventClicksOutsideTour,
      {
        capture: true,
      }
    );
  });
  window.scrollTo(0, lockedScrollY);
}

function resolveTourUser(store) {
  const userState = store?.state?.user || {};
  const authenticatedUser = userState?.authenticatedUser || {};
  const candidates = [
    authenticatedUser?.username,
    authenticatedUser?.email,
    userState?.userName,
    userState?.username,
  ];

  const resolved = candidates.find(
    (value) => typeof value === 'string' && value.trim() !== ''
  );

  return resolved ? resolved.trim() : 'anonymous';
}

function getStorageKey(store) {
  return `${ISSUED_INVOICES_GUIDED_TOUR_STORAGE_KEY}:${resolveTourUser(
    store
  )}`;
}

function getBackendTourPeriodKey(store) {
  const videoPeriodState = store?.state?.videoPeriodModule || {};

  if (!videoPeriodState.isFacturas) {
    return '';
  }

  const periodIndex =
    videoPeriodState.periodIndex === null ||
    videoPeriodState.periodIndex === undefined
      ? ''
      : String(videoPeriodState.periodIndex);
  const videoIndex =
    videoPeriodState.videoIndex === null ||
    videoPeriodState.videoIndex === undefined
      ? ''
      : String(videoPeriodState.videoIndex);
  const videoName =
    typeof videoPeriodState.currentVideo?.name === 'string'
      ? videoPeriodState.currentVideo.name.trim()
      : '';

  return [periodIndex, videoIndex, videoName || 'issued-invoices']
    .filter(Boolean)
    .join(':');
}

function toTimestamp(value) {
  return Number.isFinite(value) && value > 0 ? value : null;
}

function normalizeTourState(rawState) {
  if (!rawState || typeof rawState !== 'object') {
    return null;
  }

  const completedAt = toTimestamp(rawState.completedAt);
  const dismissedAt = toTimestamp(rawState.dismissedAt);
  const lastShownAt = toTimestamp(rawState.lastShownAt);
  const lastAutoShownAt =
    toTimestamp(rawState.lastAutoShownAt) ||
    lastShownAt ||
    dismissedAt ||
    completedAt;
  const legacyAutoShownCount = rawState.dismissed ? 1 : 0;
  const autoShownCount = Math.max(
    0,
    Number.isFinite(rawState.autoShownCount)
      ? rawState.autoShownCount
      : legacyAutoShownCount
  );
  const inProgressAutoReshowCount = Math.max(
    0,
    Number.isFinite(rawState.inProgressAutoReshowCount)
      ? rawState.inProgressAutoReshowCount
      : 0
  );

  let status =
    typeof rawState.status === 'string' ? rawState.status : null;

  if (!status) {
    if (completedAt) {
      status = 'completed';
    } else if (rawState.dismissed || dismissedAt) {
      status = 'dismissed';
    }
  }

  return {
    ...rawState,
    completedAt,
    dismissedAt,
    lastShownAt,
    lastAutoShownAt,
    autoShownCount,
    inProgressAutoReshowCount,
    status,
  };
}

function readTourState(store) {
  try {
    const raw = localStorage.getItem(getStorageKey(store));
    const state = raw ? normalizeTourState(JSON.parse(raw)) : null;
    const backendTourPeriodKey = getBackendTourPeriodKey(store);

    if (
      state &&
      backendTourPeriodKey &&
      state.tourPeriodKey !== backendTourPeriodKey
    ) {
      return null;
    }

    return state;
  } catch (error) {
    return null;
  }
}

function writeTourState(store, payload) {
  try {
    const currentState = readTourState(store) || {};
    const backendTourPeriodKey = getBackendTourPeriodKey(store);
    localStorage.setItem(
      getStorageKey(store),
      JSON.stringify({
        ...currentState,
        ...payload,
        ...(backendTourPeriodKey
          ? { tourPeriodKey: backendTourPeriodKey }
          : {}),
      })
    );
  } catch (error) {
    // No-op
  }
}

function hasCompletedTour(state) {
  return !!(
    state &&
    (state.status === 'completed' || state.completedAt)
  );
}

function hasInProgressTour(state) {
  return !!(state && state.status === 'in_progress');
}

function hasReachedInProgressAutoReshowLimit(state) {
  return (
    (state?.inProgressAutoReshowCount || 0) >=
    ISSUED_INVOICES_GUIDED_TOUR_MAX_IN_PROGRESS_AUTO_RESHOWS
  );
}

function hasReachedAutoShowLimit(state) {
  return (
    (state?.autoShownCount || 0) >=
    ISSUED_INVOICES_GUIDED_TOUR_MAX_AUTO_SHOWS_WITHOUT_COMPLETION
  );
}

function hasReshowCooldownExpired(state) {
  if (!state?.lastAutoShownAt) {
    return true;
  }

  return (
    Date.now() - state.lastAutoShownAt >=
    ISSUED_INVOICES_GUIDED_TOUR_RESHOW_INTERVAL_DAYS * DAY_IN_MS
  );
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function nextFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

async function settleUi(vm, delay = 0) {
  if (vm && typeof vm.$nextTick === 'function') {
    await new Promise((resolve) => vm.$nextTick(resolve));
  }

  await nextFrame();
  await nextFrame();

  if (delay > 0) {
    await sleep(delay);
  }
}

function getVisibleElement(selector) {
  const element = document.querySelector(selector);
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0 ? element : null;
}

function getTourTranslation(vm, key) {
  const value = vm?.$t(`issuedInvoicesGuidedTour.${key}`);
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeTourAssetPath(assetPath) {
  return assetPath
    .replace(/^@\/static\//, '')
    .replace(/^\/?src\/static\//, '')
    .replace(/^\/?static\//, '')
    .replace(/^\.?\//, '');
}

function getBundledTourAsset(assetPath) {
  const normalizedAssetPath = normalizeTourAssetPath(assetPath);
  const [assetGroup, ...assetFileParts] =
    normalizedAssetPath.split('/');
  const assetFile = assetFileParts.join('/');
  const assetContext = TOUR_ASSET_CONTEXTS[assetGroup];

  if (!assetContext || !assetFile) {
    return '';
  }

  try {
    const assetModule = assetContext(`./${assetFile}`);
    return assetModule?.default || assetModule || '';
  } catch (error) {
    return '';
  }
}

function resolveTourImageSrc(assetPath) {
  const normalizedAssetPath =
    typeof assetPath === 'string' ? assetPath.trim() : '';

  if (!TOUR_IMAGE_FILE_PATTERN.test(normalizedAssetPath)) {
    return '';
  }

  if (/^(https?:|data:|blob:)/i.test(normalizedAssetPath)) {
    return normalizedAssetPath;
  }

  const bundledAsset = getBundledTourAsset(normalizedAssetPath);
  if (bundledAsset) {
    return bundledAsset;
  }

  const isLocalTourAsset =
    /^(@\/static\/|\/?src\/static\/|\/?static\/|\.?\/?img\/)/.test(
      normalizedAssetPath
    );
  const hasUnsupportedProtocol = /^[a-z][a-z\d+.-]*:/i.test(
    normalizedAssetPath
  );

  return isLocalTourAsset || hasUnsupportedProtocol
    ? ''
    : normalizedAssetPath;
}

function getTourStepImageSrc(vm, stepKey) {
  return (
    resolveTourImageSrc(
      getTourTranslation(vm, `${stepKey}.imageSrc`)
    ) || resolveTourImageSrc(TOUR_STEP_IMAGE_FALLBACKS[stepKey])
  );
}

async function waitForVisibleElement(selector) {
  const startedAt = Date.now();

  const poll = async () => {
    const element = getVisibleElement(selector);
    if (element) {
      return element;
    }

    if (Date.now() - startedAt >= TOUR_WAIT_TIMEOUT) {
      throw new Error(`driver_selector_timeout:${selector}`);
    }

    await sleep(TOUR_WAIT_INTERVAL);
    return poll();
  };

  return poll();
}

function createStep({
  id,
  element,
  title,
  description,
  side = 'bottom',
  align = 'start',
  imageSrc = '',
  imageAlt = '',
  smallImagePopover = false,
  onNext,
  disablePrevious = false,
}) {
  return {
    id,
    element,
    onNext,
    disablePrevious,
    imageSrc,
    imageAlt,
    smallImagePopover,
    popover: {
      title,
      description,
      side,
      align,
      disableButtons: disablePrevious ? ['previous'] : [],
    },
  };
}

function renderSkipButton(popover, label, onSkip) {
  const footerButtons = popover?.footerButtons;
  if (!footerButtons) return;

  const existingButton = footerButtons.querySelector(
    `.${TOUR_SKIP_BUTTON_CLASS}`
  );
  if (existingButton) {
    existingButton.textContent = label;
    return;
  }

  const skipButton = document.createElement('button');
  skipButton.type = 'button';
  skipButton.className = TOUR_SKIP_BUTTON_CLASS;
  skipButton.textContent = label;
  skipButton.addEventListener('click', onSkip);
  footerButtons.prepend(skipButton);
}

function ensureTourPopoverStyles() {
  if (document.getElementById(TOUR_POPOVER_STYLES_ID)) {
    return;
  }

  const styleTag = document.createElement('style');
  styleTag.id = TOUR_POPOVER_STYLES_ID;
  styleTag.textContent = `
    .${TOUR_POPOVER_CLASS} {
      max-width: 500px;
      padding: 18px 18px 14px;
      border: 1px solid #cfe0f4;
      border-radius: 18px;
      box-shadow: 0 10px 42px rgba(0, 52, 106, 0.3);
      background:
        radial-gradient(circle at top right, rgba(133, 187, 35, 0.16), transparent 34%),
        linear-gradient(180deg, #ffffff 0%, #f5f9ff 100%);
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-arrow {
      border-color: #ffffff;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn {
      color: #7f9dbd;
      top: 10px;
      right: 10px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn:focus {
      color: #00346a;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-title {
      margin: 0;
      color: #00346a;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.35;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-description {
      margin-top: 8px;
      color: #35506d;
      font-size: 13px;
      line-height: 1.55;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_POPOVER_WITH_IMAGE_CLASS} {
      width: min(1260px, calc(100vw - 56px));
      max-width: min(1260px, calc(100vw - 56px));
      transform: translateY(-40px);
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_POPOVER_WITH_IMAGE_CLASS} .driver-popover-description {
      max-width: none;
    }

    .issued-invoices-tour-popover-step-image-wrap {
      margin-top: 12px;
      border-radius: 12px;
      overflow: hidden;
      border: 1px solid rgba(0, 52, 106, 0.12);
      background: #ffffff;
    }

    .issued-invoices-tour-popover-step-image {
      display: block;
      width: 100%;
      max-height: min(62vh, 560px);
      object-fit: contain;
      background: #ffffff;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} {
      width: min(520px, calc(100vw - 56px));
      max-width: min(520px, calc(100vw - 56px));
      transform: none;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} .issued-invoices-tour-popover-step-image-wrap {
      display: flex;
      justify-content: center;
      padding: 10px;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} .issued-invoices-tour-popover-step-image {
      width: auto;
      max-width: 100%;
      max-height: 80px;
    }

    @media (min-width: 769px) and (max-height: 900px) {
      .${TOUR_POPOVER_CLASS}.${TOUR_POPOVER_WITH_IMAGE_CLASS} {
        width: min(1120px, calc(100vw - 40px));
        max-width: min(1120px, calc(100vw - 40px));
        transform: translateY(-56px);
      }

      .issued-invoices-tour-popover-step-image {
        max-height: min(48vh, 400px);
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} {
        width: min(520px, calc(100vw - 40px));
        max-width: min(520px, calc(100vw - 40px));
        transform: none;
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} .issued-invoices-tour-popover-step-image {
        max-height: 72px;
      }
    }

    @media (max-width: 768px) {
      .${TOUR_POPOVER_CLASS}.${TOUR_POPOVER_WITH_IMAGE_CLASS} {
        width: calc(100vw - 20px);
        max-width: calc(100vw - 20px);
        transform: translateY(-22px);
      }

      .issued-invoices-tour-popover-step-image {
        max-height: min(46vh, 420px);
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} {
        width: calc(100vw - 20px);
        max-width: calc(100vw - 20px);
        transform: none;
      }
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer {
      margin-top: 16px;
      padding-top: 12px;
      border-top: 1px solid rgba(0, 52, 106, 0.08);
      gap: 8px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-navigation-btns {
      gap: 6px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer button {
      padding: 7px 12px;
      border-radius: 10px;
      border: 1px solid #c9d9ea;
      background: #ffffff;
      color: #00346a;
      font-size: 12px;
      font-weight: 600;
      text-shadow: none;
      box-shadow: none;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer button:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-footer button:focus {
      background: #eef5fc;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn {
      border-color: #00346a;
      background: #00346a;
      color: #ffffff;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn:focus {
      background: #04509f;
    }

    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS} {
      border-color: transparent;
      background: transparent;
      color: #6e89a6;
    }

    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS}:hover,
    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS}:focus {
      background: rgba(4, 80, 159, 0.08);
      color: #00346a;
    }

    .issued-invoices-tour-popover-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      padding-right: 28px;
    }

    .issued-invoices-tour-popover-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: linear-gradient(180deg, #ffffff 0%, #eaf3ff 100%);
      border: 1px solid #cfe0f4;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .issued-invoices-tour-popover-avatar img {
      width: 36px;
      height: 36px;
      display: block;
    }

    .issued-invoices-tour-popover-meta {
      min-width: 0;
    }

    .issued-invoices-tour-popover-name {
      color: #00346a;
      font-size: 13px;
      font-weight: 700;
      line-height: 1.2;
    }

    .issued-invoices-tour-popover-role {
      margin-top: 2px;
      color: #6e89a6;
      font-size: 11px;
      font-weight: 500;
      line-height: 1.2;
    }
  `;

  document.head.appendChild(styleTag);
}

function enhancePopoverWithAssistant(
  popover,
  { title, assistantName, helperLabel, step, vm }
) {
  const wrapper = popover?.wrapper;
  if (!wrapper) return;

  ensureTourPopoverStyles();
  wrapper.classList.add(TOUR_POPOVER_CLASS);
  wrapper.classList.toggle(
    TOUR_SMALL_IMAGE_POPOVER_CLASS,
    Boolean(step?.smallImagePopover)
  );

  if (
    !wrapper.querySelector('.issued-invoices-tour-popover-header')
  ) {
    const header = document.createElement('div');
    header.className = 'issued-invoices-tour-popover-header';
    header.innerHTML = `
      <div class="issued-invoices-tour-popover-avatar">
        <img src="${tourAssistantIcon}" alt="${assistantName}">
      </div>
      <div class="issued-invoices-tour-popover-meta">
        <div class="issued-invoices-tour-popover-name">${assistantName}</div>
        <div class="issued-invoices-tour-popover-role">${helperLabel}</div>
      </div>
    `;

    wrapper.insertBefore(header, wrapper.firstChild);
  }

  if (popover.title && title) {
    popover.title.textContent = title;
  }

  renderPopoverStepImage(popover, step, vm);
}

function renderPopoverStepImage(popover, step = null, vm = null) {
  const wrapper = popover?.wrapper;
  const description = popover?.description;
  if (!wrapper || !description) return;

  const existingWrap = wrapper.querySelector(
    '.issued-invoices-tour-popover-step-image-wrap'
  );
  const normalizedImageSrc =
    typeof step?.imageSrc === 'string' ? step.imageSrc.trim() : '';

  if (!normalizedImageSrc) {
    wrapper.classList.remove(TOUR_POPOVER_WITH_IMAGE_CLASS);
    existingWrap?.remove();
    return;
  }

  wrapper.classList.add(TOUR_POPOVER_WITH_IMAGE_CLASS);

  let imageWrap = existingWrap;
  if (!imageWrap) {
    imageWrap = document.createElement('div');
    imageWrap.className =
      'issued-invoices-tour-popover-step-image-wrap';
    description.insertAdjacentElement('afterend', imageWrap);
  }

  let image = imageWrap.querySelector('img');
  if (!image) {
    image = document.createElement('img');
    image.className = 'issued-invoices-tour-popover-step-image';
    imageWrap.appendChild(image);
  }

  image.src = normalizedImageSrc;
  image.alt =
    step?.imageAlt ||
    step?.title ||
    vm?.$t('issuedInvoicesGuidedTour.defaultImageAlt');
}

function getListActionsStepElement() {
  return (
    getVisibleElement('.issued-invoices-detail-button') ||
    getVisibleElement('.issued-invoices-download-button') ||
    getVisibleElement('[data-tour-id="issued-invoices-grid"]')
  );
}

function getDetailsSearchStepElement() {
  return (
    getVisibleElement(
      '[data-tour-id="issued-invoices-details-search"]'
    ) ||
    getVisibleElement(
      '[data-tour-id="issued-invoices-details-summary"]'
    )
  );
}

function getDashboardEntryStepElement() {
  return (
    getVisibleElement(ISSUED_INVOICES_DASHBOARD_BUTTON_SELECTOR) ||
    getVisibleElement(ISSUED_INVOICES_TOP_MENU_OPTION_SELECTOR) ||
    ISSUED_INVOICES_TOP_MENU_OPTION_SELECTOR
  );
}

function buildDashboardIntroSteps(vm, { onContinueToList } = {}) {
  const t = (key) => vm.$t(`issuedInvoicesGuidedTour.${key}`);

  return [
    createStep({
      id: 'issued-invoices-dashboard-intro',
      element: getDashboardEntryStepElement(),
      title: t('dashboardIntro.title'),
      description: t('dashboardIntro.description'),
      side: 'bottom',
      align: 'center',
      disablePrevious: true,
      onNext: async (driverObj) => {
        if (typeof onContinueToList === 'function') {
          await onContinueToList(driverObj);
          return;
        }

        markPendingIssuedInvoicesListTourStart();
        const entryElement =
          getVisibleElement(
            ISSUED_INVOICES_DASHBOARD_BUTTON_SELECTOR
          ) ||
          getVisibleElement(ISSUED_INVOICES_TOP_MENU_OPTION_SELECTOR);

        entryElement?.click();
        if (driverObj.isActive()) {
          driverObj.destroy();
        }
      },
    }),
  ];
}

function buildIssuedInvoicesListSteps(
  vm,
  { onContinueToDetails } = {}
) {
  const t = (key) => vm.$t(`issuedInvoicesGuidedTour.${key}`);
  const steps = [
    createStep({
      id: 'issued-invoices-list-intro',
      element: '[data-tour-id="issued-invoices-page-root"]',
      title: t('listIntro.title'),
      description: t('listIntro.description'),
      side: 'bottom',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'listIntro'),
      imageAlt: t('listIntro.imageAlt'),
      disablePrevious: true,
    }),
    createStep({
      id: 'issued-invoices-filter-button',
      element: '[data-tour-id="issued-invoices-filter-button"]',
      title: t('filtersButton.title'),
      description: t('filtersButton.description'),
      side: 'left',
      onNext: async (driverObj) => {
        await vm.openTourFiltersPanel?.();
        await settleUi(vm, 180);
        await waitForVisibleElement(
          '[data-tour-id="issued-invoices-date-range-filter"]'
        );

        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      id: 'issued-invoices-date-range-filter',
      element: '[data-tour-id="issued-invoices-date-range-filter"]',
      title: t('dateRange.title'),
      description: t('dateRange.description'),
      side: 'left',
      disablePrevious: true,
      onNext: async (driverObj) => {
        await vm.closeTourFiltersPanel?.();
        await settleUi(vm, 200);
        await waitForVisibleElement(
          '[data-tour-id="issued-invoices-grid"]'
        );

        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      id: 'issued-invoices-toolbar',
      element: '[data-tour-id="issued-invoices-toolbar"]',
      title: t('toolbar.title'),
      description: t('toolbar.description'),
      side: 'bottom',
    }),
  ];

  steps.push(
    createStep({
      id: 'issued-invoices-list-actions',
      element: getListActionsStepElement,
      title: t('listActions.title'),
      description: t('listActions.description'),
      side: 'left',
      imageSrc: getTourStepImageSrc(vm, 'listActions'),
      imageAlt: t('listActions.imageAlt'),
      smallImagePopover: true,
      onNext: async (driverObj) => {
        if (typeof onContinueToDetails === 'function') {
          await onContinueToDetails(driverObj);
          return;
        }

        if (driverObj.isActive()) {
          driverObj.destroy();
        }
      },
    })
  );

  return steps;
}

function buildIssuedInvoicesDetailsSteps(vm) {
  const t = (key) => vm.$t(`issuedInvoicesGuidedTour.${key}`);
  return [
    createStep({
      id: 'issued-invoices-details-summary',
      element: '[data-tour-id="issued-invoices-details-summary"]',
      title: t('detailsSummary.title'),
      description: t('detailsSummary.description'),
      side: 'bottom',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'detailsSummary'),
      imageAlt: t('detailsSummary.imageAlt'),
      disablePrevious: true,
    }),
    createStep({
      id: 'issued-invoices-details-search',
      element: getDetailsSearchStepElement,
      title: t('detailsSearch.title'),
      description: t('detailsSearch.description'),
      side: 'left',
    }),
    createStep({
      id: 'issued-invoices-details-lines',
      element: '[data-tour-id="issued-invoices-at-list"]',
      title: t('detailsLines.title'),
      description: t('detailsLines.description'),
      side: 'top',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'detailsLines'),
      imageAlt: t('detailsLines.imageAlt'),
    }),
  ];
}

function writeInProgressStartState(
  vm,
  currentState,
  startedAt,
  isAutoStart
) {
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(vm.$store, {
    dismissed: false,
    status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
    lastShownAt: startedAt,
    ...(isAutoStart
      ? {
          autoShownCount,
          inProgressAutoReshowCount,
          lastAutoShownAt: startedAt,
        }
      : {
          lastManualShownAt: startedAt,
        }),
  });

  return {
    wasPreviouslyCompleted,
    autoShownCount,
    inProgressAutoReshowCount,
  };
}

function createDriverInstance(
  vm,
  { steps, onDestroyed, isAutoStart, doneBtnTextKey = 'next' }
) {
  const t = (key) => vm.$t(`issuedInvoicesGuidedTour.${key}`);
  let completionStatus = null;
  let completionAction = null;

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText: t('next'),
    prevBtnText: t('previous'),
    doneBtnText: t(doneBtnTextKey),
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      enhancePopoverWithAssistant(popover, {
        title: popover?.title?.textContent || '',
        assistantName: t('popover.assistantName'),
        helperLabel: t('assistantHelperLabel'),
        step,
        vm,
      });
      renderSkipButton(popover, t('skip'), () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      onDestroyed({
        completionStatus,
        completionAction,
        isAutoStart,
      });
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  return driverObj;
}

export function shouldAutoStartIssuedInvoicesGuidedTour(
  store,
  { force = false } = {}
) {
  if (!ENABLE_ISSUED_INVOICES_GUIDED_TOUR) return false;
  if (force) return true;

  const state = readTourState(store);
  if (!state) return true;
  if (hasInProgressTour(state)) {
    if (!hasReachedInProgressAutoReshowLimit(state)) {
      return true;
    }

    writeTourState(store, {
      dismissed: true,
      status: 'dismissed',
      dismissedAt: Date.now(),
    });
    return false;
  }
  if (!hasCompletedTour(state) && hasReachedAutoShowLimit(state)) {
    return false;
  }

  return hasReshowCooldownExpired(state);
}

export function markPendingIssuedInvoicesDetailsTourStart() {
  try {
    sessionStorage.setItem(
      ISSUED_INVOICES_TOUR_PENDING_DETAILS_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

export function markPendingIssuedInvoicesListTourStart() {
  try {
    sessionStorage.setItem(
      ISSUED_INVOICES_TOUR_PENDING_LIST_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

export function consumePendingIssuedInvoicesListTourStart() {
  try {
    const value = sessionStorage.getItem(
      ISSUED_INVOICES_TOUR_PENDING_LIST_START_KEY
    );
    if (!value) {
      return false;
    }

    sessionStorage.removeItem(
      ISSUED_INVOICES_TOUR_PENDING_LIST_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

export function consumePendingIssuedInvoicesDetailsTourStart() {
  try {
    const value = sessionStorage.getItem(
      ISSUED_INVOICES_TOUR_PENDING_DETAILS_START_KEY
    );
    if (!value) {
      return false;
    }

    sessionStorage.removeItem(
      ISSUED_INVOICES_TOUR_PENDING_DETAILS_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

export function stopIssuedInvoicesGuidedTour(ownerVm = null) {
  if (
    ownerVm &&
    activeDriverOwnerVm &&
    activeDriverOwnerVm !== ownerVm
  ) {
    return;
  }

  if (activeDriver && activeDriver.isActive()) {
    activeDriver.destroy();
    return;
  }

  activeDriver = null;
  activeDriverOwnerVm = null;
  unlockTourScroll();
}

export function startIssuedInvoicesDashboardIntroTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartIssuedInvoicesGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    return activeDriver;
  }

  const steps = buildDashboardIntroSteps(vm, {
    onContinueToList: async (driverObj) => {
      markPendingIssuedInvoicesListTourStart();
      driverObj.issuedInvoicesCompletionStatus = 'continue_to_list';

      const entryElement =
        getVisibleElement(
          ISSUED_INVOICES_DASHBOARD_BUTTON_SELECTOR
        ) ||
        getVisibleElement(ISSUED_INVOICES_TOP_MENU_OPTION_SELECTOR);

      if (entryElement) {
        entryElement.click();
      } else {
        vm.$f7router?.navigate(
          {
            name: 'issuedInvoicesList',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      }

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const { wasPreviouslyCompleted, inProgressAutoReshowCount } =
    writeInProgressStartState(
      vm,
      currentState,
      startedAt,
      isAutoStart
    );

  const driverObj = createDriverInstance(vm, {
    steps,
    isAutoStart,
    onDestroyed: ({ completionStatus }) => {
      const endedAt = Date.now();
      const finalCompletionStatus =
        driverObj.issuedInvoicesCompletionStatus || completionStatus;
      let endStatus = 'dismissed';

      if (finalCompletionStatus === 'continue_to_list') {
        endStatus = 'in_progress';
        writeTourState(vm.$store, {
          dismissed: false,
          status: 'in_progress',
          completedAt: currentState?.completedAt || null,
          dismissedAt: currentState?.dismissedAt,
          inProgressAutoReshowCount,
        });
      } else {
        endStatus =
          finalCompletionStatus === 'completed' ||
          wasPreviouslyCompleted
            ? 'completed'
            : finalCompletionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(vm.$store, {
          dismissed: endStatus === 'dismissed',
          status: endStatus,
          completedAt:
            endStatus === 'completed'
              ? currentState?.completedAt || endedAt
              : currentState?.completedAt || null,
          dismissedAt:
            endStatus === 'dismissed'
              ? endedAt
              : currentState?.dismissedAt,
          inProgressAutoReshowCount:
            endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
        });
      }

      activeDriver = null;
      activeDriverOwnerVm = null;
      unlockTourScroll();
    },
  });

  settleUi(vm, 140).then(() => {
    if (activeDriver === driverObj) {
      lockTourScroll();
      driverObj.drive();
    }
  });

  return driverObj;
}

export function startIssuedInvoicesListGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartIssuedInvoicesGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    return activeDriver;
  }

  const steps = buildIssuedInvoicesListSteps(vm, {
    onContinueToDetails: async (driverObj) => {
      let detailNavigationResult = null;

      try {
        detailNavigationResult =
          await vm.openTourIssuedInvoiceDetail?.({
            onBeforeNavigateToDetails: () => {
              markPendingIssuedInvoicesDetailsTourStart();
            },
          });
      } catch (error) {
        detailNavigationResult = { opened: false, error };
      }

      const openedDetails =
        detailNavigationResult === true ||
        detailNavigationResult?.opened === true;

      if (!openedDetails) {
        markPendingIssuedInvoicesDetailsTourStart();
        vm.$f7router?.navigate(
          {
            name: 'issuedInvoicesDetails',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      }

      driverObj.issuedInvoicesCompletionStatus =
        'continue_to_details';

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const {
    wasPreviouslyCompleted,
    autoShownCount,
    inProgressAutoReshowCount,
  } = writeInProgressStartState(
    vm,
    currentState,
    startedAt,
    isAutoStart
  );

  const driverObj = createDriverInstance(vm, {
    steps,
    isAutoStart,
    onDestroyed: ({ completionStatus, completionAction }) => {
      const endedAt = Date.now();
      const finalCompletionStatus =
        driverObj.issuedInvoicesCompletionStatus || completionStatus;
      let endStatus = 'dismissed';

      if (finalCompletionStatus === 'continue_to_details') {
        endStatus = 'in_progress';
        writeTourState(vm.$store, {
          dismissed: false,
          status: 'in_progress',
          completedAt: currentState?.completedAt || null,
          dismissedAt: currentState?.dismissedAt,
          inProgressAutoReshowCount,
        });
      } else {
        endStatus =
          finalCompletionStatus === 'completed' ||
          wasPreviouslyCompleted
            ? 'completed'
            : finalCompletionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(vm.$store, {
          dismissed: endStatus === 'dismissed',
          status: endStatus,
          completedAt:
            endStatus === 'completed'
              ? currentState?.completedAt || endedAt
              : currentState?.completedAt || null,
          dismissedAt:
            endStatus === 'dismissed'
              ? endedAt
              : currentState?.dismissedAt,
          inProgressAutoReshowCount:
            endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
        });
      }

      activeDriver = null;
      activeDriverOwnerVm = null;
      unlockTourScroll();

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        // No-op
      }
    },
  });

  settleUi(vm, 140).then(() => {
    if (activeDriver === driverObj) {
      lockTourScroll();
      driverObj.drive();
    }
  });

  return driverObj;
}

export function startIssuedInvoicesDetailsGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartIssuedInvoicesGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    if (activeDriverOwnerVm && activeDriverOwnerVm !== vm) {
      activeDriver.destroy();
    } else {
      return activeDriver;
    }
  }

  const steps = buildIssuedInvoicesDetailsSteps(vm);
  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const { wasPreviouslyCompleted, inProgressAutoReshowCount } =
    writeInProgressStartState(
      vm,
      currentState,
      startedAt,
      isAutoStart
    );

  const driverObj = createDriverInstance(vm, {
    steps,
    isAutoStart,
    doneBtnTextKey: 'finish',
    onDestroyed: ({ completionStatus }) => {
      const endedAt = Date.now();
      const endStatus =
        completionStatus === 'completed' || wasPreviouslyCompleted
          ? 'completed'
          : completionStatus === 'passive_close'
          ? currentState?.status || 'in_progress'
          : 'dismissed';

      writeTourState(vm.$store, {
        dismissed: endStatus === 'dismissed',
        status: endStatus,
        completedAt:
          endStatus === 'completed'
            ? currentState?.completedAt || endedAt
            : currentState?.completedAt || null,
        dismissedAt:
          endStatus === 'dismissed'
            ? endedAt
            : currentState?.dismissedAt,
        inProgressAutoReshowCount:
          endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
      });

      activeDriver = null;
      activeDriverOwnerVm = null;
      unlockTourScroll();
    },
  });

  settleUi(vm, 140).then(() => {
    if (activeDriver === driverObj) {
      lockTourScroll();
      driverObj.drive();
    }
  });

  return driverObj;
}
